import {
  BrandHealthGqvTimeSeries,
  BrandHealthGqvTimeSeriesObject,
  BrandHealthTableInfo,
  GqvChartRow,
  GqvMapRow
} from '@blisspointmedia/bpm-types/dist/BrandHealthMetrics';
import {
  CompanyFiscalCal,
  DateGroupingKey,
  getGroupingValue
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DateRange } from '../../utils/date-utils';

export const formatGqvQueryDataForTimeSeries = (
  filteredChartRows: GqvChartRow[]
): BrandHealthGqvTimeSeriesObject => {
  const sumsByDate: Record<string, Record<string, number>> = {};

  const uniqueLabelsSet = new Set<string>();

  for (let i = 0; i < filteredChartRows.length; i++) {
    const row = filteredChartRows[i];
    const date = row.ReportDate;
    const label = row.QueryLabel;
    const volume = Number(row.IndexedQueryVolume);

    uniqueLabelsSet.add(label);

    if (!sumsByDate[date]) {
      sumsByDate[date] = {};
    }
    if (!sumsByDate[date][label]) {
      sumsByDate[date][label] = 0;
    }
    sumsByDate[date][label] += volume;
  }

  const uniqueLabels = Array.from(uniqueLabelsSet).sort();

  const allDates = Object.keys(sumsByDate).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  const result: BrandHealthGqvTimeSeries[] = [];

  for (let i = 0; i < allDates.length; i++) {
    const date = allDates[i];
    const dateSums = sumsByDate[date];
    const obj: any = {
      ReportDate: date
    };

    for (let j = 0; j < uniqueLabels.length; j++) {
      const label = uniqueLabels[j];
      // A date with no rows for this label is filled with a hard 0, which the chart plots
      // as a real datapoint indistinguishable from a day that genuinely had no query
      // volume. Leaving it null instead would let the line gap (LineChart sets no
      // connectNulls), but calculateMovingAverage and groupFormattedGqvLineChartDataByDate
      // both coerce non-numbers to 0, so they would need to skip missing days rather than
      // average/sum them in. Not changed here.
      obj[label] = dateSums[label] || 0;
    }

    result.push(obj);
  }
  return { res: result, uniqueLabels };
};

const buildRegionPredicate = (
  filters: Partial<Record<string, boolean>>
): ((region: string) => boolean) => {
  if (filters['Region-All'] === true) {
    return () => true;
  }

  const selectedRegions = new Set<string>();
  Object.entries(filters).forEach(([region, isSelected]) => {
    if (isSelected) {
      selectedRegions.add(region);
    }
  });

  return region => selectedRegions.has(region);
};

export const filterGqvChartRows = (
  chartRows: GqvChartRow[],
  filters: Partial<Record<string, boolean>>,
  dateRange: DateRange
): GqvChartRow[] => {
  const includesRegion = buildRegionPredicate(filters);

  return chartRows.filter(
    row =>
      row.ReportDate >= dateRange.start &&
      row.ReportDate <= dateRange.end &&
      includesRegion(row.Region)
  );
};

// Map rows are month-bucketed, so the date filter works at whole-month granularity:
// every month that overlaps the selected range is included in full.
export const filterGqvMapRows = (
  mapRows: GqvMapRow[],
  filters: Partial<Record<string, boolean>>,
  dateRange: DateRange
): GqvMapRow[] => {
  const includesRegion = buildRegionPredicate(filters);
  const startMonth = dateRange.start.slice(0, 7);
  const endMonth = dateRange.end.slice(0, 7);

  return mapRows.filter(row => {
    const month = row.ReportMonth.slice(0, 7);
    return month >= startMonth && month <= endMonth && includesRegion(row.Region);
  });
};

const capitalizeDmaFullName = (input: string): string =>
  input
    .split(', ')
    .map((segment, index) => {
      if (index === 0) {
        return segment
          .split(' ')
          .map(word =>
            word
              .split('-')
              .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join('-')
          )
          .join(' ');
      }
      return segment.toUpperCase();
    })
    .join(', ');

export const groupGqvMapRowsByDmaCode = (data: GqvMapRow[]): BrandHealthTableInfo[] => {
  const aggregation: Record<string, BrandHealthTableInfo> = {};

  data.forEach(item => {
    const { dma_code } = item;
    if (!dma_code) {
      return;
    }

    if (!aggregation[dma_code]) {
      aggregation[dma_code] = {
        dma_full_name: capitalizeDmaFullName(item.dma_full_name),
        dma_code,
        'Absolute Index': 0,
        Population: item.population
      };
    }

    aggregation[dma_code]['Absolute Index'] += Number(item.IndexedQueryVolume);
  });

  const groupedByDmaCode = Object.values(aggregation);
  // DMAs without a population can neither contribute to the average nor receive an
  // adjusted index of their own.
  const withPopulation = groupedByDmaCode.filter(obj => obj.Population);
  const avgIndexPerPopulation =
    withPopulation.reduce(
      (sum, obj) => sum + obj['Absolute Index'] / (obj.Population as number),
      0
    ) / withPopulation.length;
  return groupedByDmaCode.map(obj => ({
    ...obj,
    'Absolute Index Adjusted':
      obj.Population && avgIndexPerPopulation > 0
        ? obj['Absolute Index'] / obj.Population / avgIndexPerPopulation
        : null
  }));
};

export const groupGqvQueryDataByDmaCodeAndQueryLabel = (data: GqvMapRow[]): any[] => {
  const aggregation: Record<string, Record<string, any>> = {};

  data.forEach(item => {
    const { dma_code, QueryLabel, IndexedQueryVolume, dma_full_name, population } = item;

    if (!dma_code || !QueryLabel) {
      return;
    }

    if (!aggregation[dma_code]) {
      aggregation[dma_code] = {
        dma_full_name: capitalizeDmaFullName(dma_full_name),
        dma_code,
        Population: population
      };
    }

    if (!aggregation[dma_code][QueryLabel]) {
      aggregation[dma_code][QueryLabel] = 0;
    }

    aggregation[dma_code][QueryLabel] += Number(IndexedQueryVolume);
  });

  return Object.values(aggregation).map(entry => {
    const { dma_full_name, dma_code, Population, ...queryLabelData } = entry;

    return {
      'DMA Full Name': dma_full_name,
      'DMA Code': dma_code,
      Population,
      ...queryLabelData
    };
  });
};

export const returnTitleForGqvChart = (typeOfChart: string): string => {
  switch (typeOfChart) {
    case 'showGraph':
      return 'Indexed Google Query Volume';
    case 'showTable':
      return 'Total Google Query Volume';
    case 'showMap':
      return 'Indexed Google Query Volume by DMA';
    default:
      return 'Indexed Google Query Volume';
  }
};

export const filterTableDataByLabel = <T extends { QueryLabel: string }>(
  rows: T[],
  label: string
): T[] => rows.filter(row => row.QueryLabel === label);

export const groupFormattedGqvLineChartDataByDate = (
  formattedGqvLineChartData: BrandHealthGqvTimeSeries[],
  groupingOption: DateGroupingKey,
  fiscalCal: CompanyFiscalCal
): BrandHealthGqvTimeSeries[] => {
  const groups: Record<string, BrandHealthGqvTimeSeries> = {};

  formattedGqvLineChartData.forEach((item: BrandHealthGqvTimeSeries) => {
    const groupKey = getGroupingValue(item.ReportDate, groupingOption, fiscalCal);

    if (!groups[groupKey]) {
      groups[groupKey] = { ReportDate: groupKey };
    }

    for (const [key, value] of Object.entries(item)) {
      if (key !== 'ReportDate' && typeof value === 'number') {
        groups[groupKey][key] = ((groups[groupKey][key] as number) || 0) + value;
      }
    }
  });

  return Object.values(groups);
};

// Significant digits rather than fixed decimals, because the two series on this chart are
// orders of magnitude apart: Category Terms runs in the thousands while Brand Terms runs
// around 0.000045, which a fixed two decimals renders as "0.00".
//
// A '<0.01' threshold label — the convention in formatAndRoundNumber and the GeoMMM
// formatters — is wrong here for the same reason: every Brand Terms point is below 0.01,
// so all of them would read identically and the tooltip would carry no information at all.
// Those formatters serve spend and count metrics that sit near or above 1; this one has to
// stay readable at any magnitude.
const gqvTooltipFormatter = new Intl.NumberFormat('en-us', { maximumSignificantDigits: 3 });

export const formatGqvTooltipValue = (num: number | null): string => {
  if (num == null || Number.isNaN(num)) {
    return '--';
  }

  if (num === 0) {
    return '0';
  }

  // At or above 1, two decimals matches how every other value on this page reads.
  if (Math.abs(num) >= 1) {
    return formatNumber(num, 2);
  }

  return gqvTooltipFormatter.format(num);
};

export const formatAndRoundNumber = (num: number | null): string => {
  if (num == null) {
    return '';
  }

  if (num === 0) {
    return '0';
  }

  if (Math.abs(num) < 0.5) {
    return '<1';
  }

  const roundedValue = Math.round(num);

  return roundedValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

export const calculateMovingAverage = (
  groupByDateGqvData: BrandHealthGqvTimeSeries[],
  uniqueLabels: string[],
  movingAvgDaysGqv: string
): BrandHealthGqvTimeSeries[] => {
  if (movingAvgDaysGqv === '1') {
    return groupByDateGqvData;
  }

  const movingAvg = parseInt(movingAvgDaysGqv, 10);
  const sortedData = [...groupByDateGqvData].sort(
    (a, b) => new Date(a.ReportDate).getTime() - new Date(b.ReportDate).getTime()
  );

  return sortedData.map((entry, index) => {
    const newEntry = { ...entry };

    uniqueLabels.forEach(label => {
      const start = Math.max(0, index - movingAvg + 1);
      const values = sortedData
        .slice(start, index + 1)
        .map(item => (typeof item[label] === 'number' ? item[label] : 0));

      const movingAverage =
        values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

      newEntry[label] = movingAverage;
    });

    return newEntry;
  });
};
